import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Divider,
  Paper,
  Typography,
  Button,
  Box
} from '@mui/material';

const ExitModal = ({
  open,
  onClose,
  selectedEmp,
  exitForm,
  setExitForm,
  handleStatusChange,
  submitExit,
  desigMap,
  deptMap,
  exitReasons
}) => {

  const readOnlyStyle = {
    bgcolor: '#f8f9fa',
    color: '#6c757d',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0'
    },
    '& .MuiInputBase-root': {
      height: 38,
      fontSize: '13px'
    },
    '& .MuiInputBase-input': {
      fontSize: '13px'
    }
  };

  const selectProps = {
    displayEmpty: true,
    disablePortal: true,
    MenuProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
      sx: {
        zIndex: 9999,
      }
    }
  };

  const fieldSx = {
    '& .MuiInputBase-root': {
      height: 38,
      fontSize: '13px',
    },
    '& .MuiInputBase-input': {
      fontSize: '13px',
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
    }
  };

  const remarksSx = {
    '& .MuiInputBase-root': {
      fontSize: '13px',
      alignItems: 'flex-start',
      minHeight: 95,
    },
    '& .MuiInputBase-input': {
      fontSize: '13px',
    }
  };

  const clearDisabledStyle = {
    '& .MuiInputBase-root.Mui-disabled': {
      bgcolor: '#f8f9fa'
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 'bold',
          color: 'white',
          bgcolor: '#d32f2f',
          py: 2,
          fontSize: '18px'
        }}
      >
        Process Employee Exit
      </DialogTitle>

      <DialogContent sx={{ mt: 3, p: 4, bgcolor: '#fafafa' }}>
        {selectedEmp && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              bgcolor: '#fff'
            }}
          >
            {/* Employee Identity */}
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.secondary',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Employee Identity (Read-Only)
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Emp ID"
                  value={selectedEmp.emp_id || ''}
                  InputProps={{ readOnly: true }}
                  sx={readOnlyStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Name"
                  value={`${selectedEmp.first_name || ''} ${selectedEmp.last_name || ''}`}
                  InputProps={{ readOnly: true }}
                  sx={readOnlyStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Designation"
                  value={desigMap[selectedEmp.designation_id] || selectedEmp.role || 'N/A'}
                  InputProps={{ readOnly: true }}
                  sx={readOnlyStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Department"
                  value={deptMap[selectedEmp.department_id] || 'N/A'}
                  InputProps={{ readOnly: true }}
                  sx={readOnlyStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Date of Joining"
                  value={
                    selectedEmp.date_of_joining
                      ? new Date(selectedEmp.date_of_joining).toLocaleDateString()
                      : 'N/A'
                  }
                  InputProps={{ readOnly: true }}
                  sx={readOnlyStyle}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Exit Configuration */}
            <Typography
              variant="subtitle2"
              sx={{
                color: 'primary.main',
                fontWeight: 'bold',
                mb: 2
              }}
            >
              Exit Configuration
            </Typography>

            {/* Row 1 - Status, Reason, Last Working Day */}
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={exitForm.status}
                  onChange={handleStatusChange}
                  SelectProps={selectProps}
                  sx={fieldSx}
                >
                  <MenuItem value="" disabled>
                    <em>Select Status</em>
                  </MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="RESIGNED">Resigned</MenuItem>
                  <MenuItem value="NOTICE">Notice</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={exitForm.reason_id}
                  onChange={(e) =>
                    setExitForm({ ...exitForm, reason_id: e.target.value })
                  }
                  disabled={!exitForm.status}
                  SelectProps={selectProps}
                  sx={{
                    ...fieldSx,
                    ...clearDisabledStyle
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Select Reason</em>
                  </MenuItem>
                  {exitReasons.map(r => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.reason}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  label="Last Working Day (DOE) *"
                  InputLabelProps={{ shrink: true }}
                  value={exitForm.exit_date}
                  onChange={(e) =>
                    setExitForm({ ...exitForm, exit_date: e.target.value })
                  }
                  sx={fieldSx}
                />
              </Grid>
            </Grid>

            {/* Row 2 - Additional Details Full Width */}
            <Box sx={{ mt: 3 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={4}
                label="Additional Details & Remarks (Optional)"
                value={exitForm.details}
                onChange={(e) =>
                  setExitForm({ ...exitForm, details: e.target.value })
                }
                placeholder="Type any specific remarks, handover details, asset recovery notes, or HR comments here..."
                sx={remarksSx}
              />
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pr: 4, pb: 3, bgcolor: '#fafafa' }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ fontWeight: 'bold' }}
        >
          Cancel
        </Button>

        <Button
          onClick={submitExit}
          variant="contained"
          color="error"
          disableElevation
          sx={{
            fontWeight: 'bold',
            px: 4,
            borderRadius: '8px'
          }}
        >
          Confirm Exit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExitModal;